import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiClient, call } from "../api/client";
import { assignmentLabel, isAtcRole, personCrewLabel, roleLabel, rolePickerLabel, setCurrentCrewTable, sortRoles, splitRoles, type CrewLabelTable } from "./crewLabels";

export const crewLabelsQueryKey = ["crew-labels"] as const;

/**
 * `GET /crew-labels`: the same for everyone and fixed for a release, so it is read once per session. The session gate
 * waits for it (success or failure) before any screen renders, so pure text builders never run before the table is in.
 */
export function useCrewLabelTable(options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    // One answer per session: a failed read is not retried by every screen that mounts (roles then read as their keys).
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    queryKey: crewLabelsQueryKey,
    queryFn: async () => {
      const table = await call(apiClient.GET("/crew-labels"));
      setCurrentCrewTable(table);
      return table;
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });
}

/** An empty table until the server answers: every role then shows its key, never a label the server did not give. */
const EMPTY: CrewLabelTable = { roles: [], positionTags: [] };

/** The crew label helpers bound to the server's table (see `crewLabels.ts`). */
export function useCrewLabels() {
  const query = useCrewLabelTable();
  const table = query.data ?? EMPTY;
  return useMemo(
    () => ({
      ready: query.data !== undefined,
      table,
      label: (role: string) => roleLabel(table, role),
      pickerLabel: (role: string) => rolePickerLabel(table, role),
      personLabel: (roles: readonly string[], position?: string | null) => personCrewLabel(table, roles, position),
      assignmentLabel: (role: string, position?: string | null) => assignmentLabel(table, role, position),
      sortRoles: <T extends string>(roles: readonly T[]) => sortRoles(table, roles),
      splitRoles: <T extends string>(roles: readonly T[]) => splitRoles(table, roles),
      isAtc: (role: string) => isAtcRole(table, role),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `table` is derived from query.data
    [query.data],
  );
}
